import { ReactNode } from 'react';

import './TextLink.css';

export enum TextLinkSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export enum TextLinkIconPosition {
  LEFT = 'left',
  RIGHT = 'right',
}

export interface TextLinkProps {
  /**
   * Link text to display
   */
  text: string;
  /**
   * Callback when the link is clicked
   */
  onClick: () => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Whether to show underline on the link
   * @default true
   */
  underline?: boolean;
  /**
   * Size variant of the link text
   * @default TextLinkSize.LARGE
   */
  size?: TextLinkSize;
  /**
   * Optional icon element to display alongside the text
   */
  icon?: ReactNode;
  /**
   * Position of the icon relative to the text
   * @default TextLinkIconPosition.RIGHT
   */
  iconPosition?: TextLinkIconPosition;
  /**
   * Accessible label for screen readers
   */
  ariaLabel?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

export function TextLink({
  text,
  onClick,
  className = '',
  underline = true,
  size = TextLinkSize.LARGE,
  icon,
  iconPosition = TextLinkIconPosition.RIGHT,
  ariaLabel,
  'data-testid': dataTestId,
}: TextLinkProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onClick();
  };

  const sizeClass = `text-link--${size}`;
  const underlineClass = underline ? '' : 'text-link--no-underline';
  const linkClassName = `text-link ${sizeClass} ${underlineClass} ${className}`.trim();

  const baseTestId = dataTestId ?? 'text-link';
  const iconElement = icon ? (
    <span
      className='text-link__icon'
      data-testid={`${baseTestId}-icon`}>
      {icon}
    </span>
  ) : null;

  return (
    <a
      aria-label={ariaLabel}
      className={linkClassName}
      data-testid={baseTestId}
      href='#'
      onClick={handleClick}>
      {iconPosition === TextLinkIconPosition.LEFT && iconElement}
      {text && (
        <span
          className='text-link__text'
          data-testid={`${baseTestId}-text`}>
          {text}
        </span>
      )}
      {iconPosition === TextLinkIconPosition.RIGHT && iconElement}
    </a>
  );
}
